import { useState, useEffect } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Dashboard from "@/pages/Dashboard";
import Scenarios from "@/pages/Scenarios";
import TrainingChat from "@/pages/TrainingChat";
import History from "@/pages/History";
import RandomCall from "@/pages/RandomCall";
import AssignedCalls from "@/pages/AssignedCalls";
import RAOTraining from "@/pages/RAOTraining";
import RAOAdmin from "@/pages/RAOAdmin";
import Retroaction from "@/pages/Retroaction";
import AutoEvaluation from "@/pages/AutoEvaluation";
import QRDisplay from "@/pages/QRDisplay";
import Health from "@/pages/Health";
import TeamExercise from "@/pages/TeamExercise";
import Appointments from "@/pages/Appointments";
import Help from "@/pages/Help";
import Progress from "@/pages/Progress";
import MobileGuide from "@/pages/MobileGuide";
import DemoShowcase from "@/pages/DemoShowcase";
import FloatingComment from "@/components/FloatingComment";
import Navigation from "@/components/Navigation";
import Login from "@/components/Login";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// --- Global auth: attach JWT to every request; force re-login on 401 ---
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('sim_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401 && localStorage.getItem('sim_token')) {
      localStorage.removeItem('sim_token');
      localStorage.removeItem('sim_student');
      localStorage.removeItem('sim_auth_timestamp');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

function FullApp() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [studentInfo, setStudentInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [appStatus, setAppStatus] = useState({ active: true, message: '' });

  useEffect(() => {
    const studentData = localStorage.getItem('sim_student');
    const authTimestamp = localStorage.getItem('sim_auth_timestamp');
    const hasToken = !!localStorage.getItem('sim_token');

    if (studentData && authTimestamp && hasToken) {
      const authTime = parseInt(authTimestamp);
      if (Date.now() - authTime < 24 * 60 * 60 * 1000) {
        setStudentInfo(JSON.parse(studentData));
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('sim_token');
        localStorage.removeItem('sim_student');
        localStorage.removeItem('sim_auth_timestamp');
      }
    }
    setIsLoading(false);
  }, []);

  // Check app status periodically (every 60s)
  useEffect(() => {
    const checkStatus = () => {
      axios.get(`${API}/app-status`).then(r => setAppStatus(r.data)).catch(() => {});
    };
    checkStatus();
    const interval = setInterval(checkStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogin = (student) => {
    localStorage.setItem('sim_student', JSON.stringify(student));
    localStorage.setItem('sim_auth_timestamp', Date.now().toString());
    setStudentInfo(student);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('sim_token');
    localStorage.removeItem('sim_student');
    localStorage.removeItem('sim_auth_timestamp');
    setStudentInfo(null);
    setIsAuthenticated(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-xl" style={{ color: '#2E6CF5' }}>Chargement...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login mode="full" onLogin={handleLogin} />;
  }

  const rawPerms = studentInfo?.permissions || {};
  // Admins always have full access to every section (including Santé) regardless of DB flags
  const perms = rawPerms.admin
    ? {
        tableau_de_bord: true,
        scenarios: true,
        appels_911: true,
        appels_assignes: true,
        historique: true,
        rao: true,
        ambulance: true,
        admin: true,
      }
    : rawPerms;

  // Enriched studentInfo passed to children — ensures pages like TrainingChat
  // see the same admin-elevated permissions as the navigation/routes.
  const effectiveStudentInfo = studentInfo
    ? { ...studentInfo, permissions: perms }
    : null;

  // First allowed route — used as redirect fallback everywhere.
  // Prevents infinite "/" <-> "/scenarios" redirect loops for restricted profiles (ex: RAO only).
  const firstAllowed = perms.tableau_de_bord !== false ? '/'
    : perms.scenarios ? '/scenarios'
    : perms.appels_911 ? '/random-call'
    : perms.appels_assignes ? '/assigned-calls'
    : perms.ambulance ? '/health'
    : perms.rao ? '/rao'
    : perms.historique ? '/history'
    : '/help';

  return (
    <>
      <Navigation
        mode="full"
        onLogout={handleLogout}
        studentName={studentInfo ? `${studentInfo.first_name} ${studentInfo.last_name}` : null}
        studentEmail={studentInfo?.email}
        permissions={perms}
      />
      {/* App inactive overlay for non-admin students */}
      {!appStatus.active && !perms.admin && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center" data-testid="app-inactive-overlay">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#FEE2E2' }}>
              <svg className="w-8 h-8" style={{ color: '#EE2130' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#002768' }}>Application fermée</h2>
            <p className="text-gray-600 mb-4">{appStatus.message || "L'application est temporairement fermée."}</p>
            {appStatus.scheduled_start && (
              <p className="text-sm" style={{ color: '#2E6CF5' }}>
                Prochaine ouverture : {new Date(appStatus.scheduled_start).toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Toronto' })}
              </p>
            )}
          </div>
        </div>
      )}
      <Routes>
        <Route path="/" element={perms.tableau_de_bord !== false ? <Dashboard /> : <Navigate to={firstAllowed} replace />} />
        <Route path="/scenarios" element={perms.scenarios ? <Scenarios /> : <Navigate to={firstAllowed} replace />} />
        <Route path="/random-call" element={perms.appels_911 ? <RandomCall studentInfo={effectiveStudentInfo} /> : <Navigate to={firstAllowed} replace />} />
        <Route path="/assigned-calls" element={perms.appels_assignes ? <AssignedCalls studentInfo={effectiveStudentInfo} /> : <Navigate to={firstAllowed} replace />} />
        <Route path="/rao" element={perms.rao ? <RAOTraining studentInfo={effectiveStudentInfo} /> : <Navigate to={firstAllowed} replace />} />
        <Route path="/health" element={perms.ambulance ? <Health studentName={studentInfo ? `${studentInfo.first_name} ${studentInfo.last_name}` : ''} studentNumber={studentInfo?.student_number || ''} isAdmin={!!perms.admin} /> : <Navigate to={firstAllowed} replace />} />
        <Route path="/equipe" element={<TeamExercise studentInfo={effectiveStudentInfo} />} />
        <Route path="/rendez-vous" element={<Appointments studentInfo={effectiveStudentInfo} isAdmin={!!perms.admin} />} />
        <Route path="/training/:scenarioId" element={perms.scenarios ? <TrainingChat studentInfo={effectiveStudentInfo} /> : <Navigate to={firstAllowed} replace />} />
        <Route path="/history" element={perms.historique ? <History studentInfo={effectiveStudentInfo} /> : <Navigate to={firstAllowed} replace />} />
        <Route path="/progress" element={perms.historique ? <Progress studentInfo={effectiveStudentInfo} /> : <Navigate to={firstAllowed} replace />} />
        <Route path="/retroaction/:sessionId" element={<Retroaction studentInfo={effectiveStudentInfo} isAdmin={!!perms.admin} />} />
        <Route path="/auto-evaluation/:sessionType/:sessionId" element={<AutoEvaluation studentInfo={effectiveStudentInfo} isAdmin={!!perms.admin} />} />
        <Route path="/qr" element={<QRDisplay />} />
        <Route path="/help" element={<Help />} />
        <Route path="/rao-admin" element={perms.admin ? <RAOAdmin /> : <Navigate to={firstAllowed} replace />} />
      </Routes>
      {studentInfo && <FloatingComment studentInfo={studentInfo} />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/m/guide/:protocolId" element={<MobileGuide />} />
          <Route path="/demonstration" element={<DemoShowcase />} />
          <Route path="/*" element={<FullApp />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <img
        src="/watermark-montmorency.png"
        alt=""
        className="cm-watermark"
        aria-hidden="true"
        draggable="false"
      />
    </div>
  );
}

export default App;
